import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AmountInput } from '../../../core/shared/directives/amount-input.directive';
import { AmountPipe } from '../../../core/shared/directives/amount.pipe';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { FinancialProfileService } from '../shared/services/financial-profile.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { DynamicFormComponent } from '../../../core/shared/dynamic-form/dynamic-form.component';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { ExcelUpload } from '../../../core/shared/components/excel-upload/excel-upload';
import {
  ExcelImportService,
  ExcelParseResult,
} from '../../../core/shared/services/excel-import.service';
import { OnboradingService } from '../../onboarding/shared/service/onboarding.service';
import { Setup } from '../../setup/shared/service/setup';
import {
  CraditService,
  CreditFinancialDataItemDto,
  SaveCreditFinancialDataPayload,
} from '../shared/services/cradit.service';

@Component({
  selector: 'app-financial-data',
  standalone: true,
  imports: [CommonModule, FormsModule, DynamicFormComponent, ExcelUpload, TranslatePipe, AmountInput, AmountPipe],
  templateUrl: './financial-data.html',
  styleUrl: './financial-data.css',
})
export class FinancialData implements OnInit, OnChanges, OnDestroy {
  /**
   * typ = 1 means Financial Data.
   */
  readonly FINANCIAL_DATA_TYPE = 1;

  @Input() config: any;
  @Input() editable = false;
  @Input() crdtPrpslSeq: number | null = null;
  @Input() sectionId = '';

  onboardingId: number | null = null;
  institutionName = '';

  demographicFields: DynamicFieldConfig[] = [
    {
    key: 'institutionName',
    label: 'FINANCIAL_DATA.INSTITUTION_NAME',
    type: 'text',
    placeholder: 'FINANCIAL_DATA.INSTITUTION_NAME_PLACEHOLDER',
    disabled: true,
    validations: {
      required: true,
    },
  },
    {
      key: 'reportingCurrency',
      label: 'FINANCIAL_DATA.REPORTING_CURRENCY',
      type: 'select',
      options$: [
        { label: 'PKR', value: 0 },
        { label: 'USD', value: 1 },
      ],
      validations: { required: true },
    },
    {
      key: 'financialYearEnd',
      label: 'FINANCIAL_DATA.FINANCIAL_YEAR_END',
      type: 'date',
      validations: { required: true },
    },
    {
      key: 'isConsolidated',
      label: 'FINANCIAL_DATA.CONSOLIDATED_FINANCIALS',
      type: 'radio',
      options$: [
        { label: 'COMMON.YES', value: true },
        { label: 'COMMON.NO', value: false },
      ],
    },
  ];

  demographicModel: any = {};

  years: string[] = [];
  availableYears: string[] = [];
  selectedYear = '';

  /**
   * Years removed via the ✕ icon that still have previously saved data —
   * carried through to `buildFinancialDataList()` so their rows go out
   * with delFlg 1 instead of silently dropping out of the payload.
   */
  private removedYears: string[] = [];

  /**
   * One Audited / Unaudited selection per financial year.
   *
   * 0 = Audited
   * 1 = Unaudited
   *
   * The selected value is shared by every financial section for that year.
   */
  financialTypeByYear: Record<string, number | null> = {};

  financialSections: any[] = [];

  /** Message shown after an Excel import. */
  fileUploadError = '';

  private saveSub?: Subscription;
  private financialMasterLoaded = false;

  constructor(
    private setupService: Setup,
    private creditService: CraditService,
    private route: ActivatedRoute,
    private onboardingService: OnboradingService,
    private cdr: ChangeDetectorRef,
    private excelImport: ExcelImportService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private financialProfileService: FinancialProfileService,
  ) {}

  ngOnInit(): void {
    this.onboardingId =
      this.getRouteSequence();

    this.generateAvailableYears();
    this.setDefaultYears();

    this.loadInstitutionName();
    this.loadFinancialMasterData();

    this.saveSub =
      this.creditService
        .saveSectionTrigger$
        .subscribe(
          (
            requestedSectionId:
              string
          ) => {
            const requestedId =
              this.normalizeSectionId(
                requestedSectionId
              );

            const componentSectionId =
              this.normalizeSectionId(
                this.sectionId
              );

            if (
              !requestedId ||
              requestedId !==
                componentSectionId
            ) {
              return;
            }

            this.saveFinancialData(
              requestedId
            );
          }
        );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['crdtPrpslSeq'] && this.financialMasterLoaded && this.crdtPrpslSeq) {
      this.loadSavedFinancialData();
    }
  }

  ngOnDestroy(): void {
    this.saveSub?.unsubscribe();
  }

private loadInstitutionName(): void {
  if (
    this.onboardingId === null ||
    this.onboardingId <= 0
  ) {
    console.error(
      'Valid onboarding sequence is not available.'
    );

    return;
  }

  this.onboardingService
    .GetOBAprvdList()
    .subscribe({
      next: (response: any) => {
        const possibleLists = [
          response,
          response?.data,
          response?.content,
          response?.data?.content,
          response?.crdtList,
          response?.data?.crdtList,
        ];

        const institutionList =
          possibleLists.find(
            (value: unknown) =>
              Array.isArray(value)
          );

        const list: any[] =
          Array.isArray(institutionList)
            ? institutionList
            : [];

        const currentInstitution =
          list.find(
            (item: any) =>
              Number(
                item?.crptOnbrdngSeq ??
                item?.crprtOnbrdngSeq ??
                item?.onboardingSeq
              ) === Number(this.onboardingId)
          );

        this.institutionName =
          String(
            currentInstitution?.nmOfInstitution ??
            currentInstitution?.instNm ??
            currentInstitution?.institutionName ??
            ''
          ).trim();

        this.demographicModel = {
          ...this.demographicModel,
          institutionName:
            this.institutionName,
        };

        console.log(
          'Loaded institution name:',
          this.institutionName
        );

        if (!this.institutionName) {
          console.warn(
            'Institution name not found for onboarding:',
            this.onboardingId,
            response
          );
        }
      },

      error: (error) => {
        console.error(
          'Unable to load institution name:',
          error
        );

        this.institutionName = '';

        this.demographicModel = {
          ...this.demographicModel,
          institutionName: '',
        };
      },
    });
}

  getRouteSequence(): number | null {
    let currentRoute: ActivatedRoute | null = this.route;

    while (currentRoute) {
      const routeId =
        currentRoute.snapshot.paramMap.get('id') ||
        currentRoute.snapshot.paramMap.get('oid');

      const sequence = Number(routeId);

      if (routeId && !Number.isNaN(sequence) && sequence > 0) {
        return sequence;
      }

      currentRoute = currentRoute.parent;
    }

    return null;
  }

  generateAvailableYears(): void {
    const currentYear = new Date().getFullYear();

    this.availableYears = [];

    for (let index = 0; index < 20; index++) {
      this.availableYears.push(String(currentYear - index));
    }
  }

  setDefaultYears(): void {
    const currentYear = new Date().getFullYear();

    this.years = [
      String(currentYear),
      String(currentYear - 1),
      String(currentYear - 2),
    ];

    this.ensureYearTypeEntries();
  }

  addYear(): void {
    if (!this.selectedYear) {
      return;
    }

    const yearToAdd = this.selectedYear;

    if (!this.years.includes(yearToAdd)) {
      this.years.push(yearToAdd);
      this.sortYears();

      if (
        this.financialTypeByYear[yearToAdd] ===
        undefined
      ) {
        this.financialTypeByYear[yearToAdd] = null;
      }

      this.syncFinancialTypeToSections(
        yearToAdd
      );
    }

    // Re-adding a previously removed year should save it normally again,
    // not carry forward the pending delFlg 1 from its earlier removal.
    this.removedYears = this.removedYears.filter(
      (year) => year !== yearToAdd,
    );

    this.selectedYear = '';
  }

  onExcelParsed(parsed: ExcelParseResult | null): void {
    const result = this.excelImport.applyPatch(
      parsed,
      this.financialSections,
      this.demographicModel,
      this.years,
    );

    this.fileUploadError = result.message;
    this.demographicModel = { ...this.demographicModel };
    this.cdr.detectChanges();
  }

  private normalizeLabel(
    value: any
  ): string {
    return String(value ?? '')
      .replace(/[-–—]/g, ' ')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  removeYear(year: string): void {
    this.years = this.years.filter(
      (item) => item !== year
    );

    if (!this.removedYears.includes(year)) {
      this.removedYears = [
        ...this.removedYears,
        year,
      ];
    }

    delete this.financialTypeByYear[year];

    this.financialSections.forEach(
      (section: any) => {
        if (section.typeByYear) {
          delete section.typeByYear[year];
        }
      }
    );
  }

  onYearFinancialTypeChange(
    year: string,
    value: number | null
  ): void {
    const normalizedType =
      this.normalizeFinancialType(value);

    this.financialTypeByYear[year] =
      normalizedType;

    this.syncFinancialTypeToSections(
      year
    );
  }

  sortYears(): void {
    this.years = this.years.sort(
      (first, second) => Number(second) - Number(first),
    );
  }

  loadFinancialMasterData(): void {
    this.setupService.getActvFinanicalType().subscribe({
      next: (types: any[]) => {
        const activeTypes = (types || []).filter(
          (item) => item.actvFlg === true && item.delFlg === 0,
        );

        const fncTypSeqs = activeTypes.map((item) => item.fncTypSeq);

        this.setupService.getActvFinanicalVal(fncTypSeqs).subscribe({
          next: (response: any) => {
            this.financialSections = activeTypes.map((type: any) => {
              const values = response?.[type.fncTypSeq] || [];

              return {
                title: type.typNm?.trim() || '',
                description: type.dscr || '',
                fncTypSeq: type.fncTypSeq,
                expanded: true,
                checked: false,
                typeByYear: this.createEmptyTypeByYear(),
                rows: values
                  .filter(
                    (value: any) =>
                      value.actvFlg === true && value.delFlg === 0,
                  )
                  .map((value: any) => ({
                    label:
                      value.dscr ||
                      value.valNm ||
                      value.fncValNm ||
                      '',
                    fncValSeq: value.fncValSeq,
                    checked: false,
                    key: `fnc_${value.fncValSeq}`,
                    recordSequenceByYear: {},
                    recordTypeByYear: {},
                  })),
              };
            });

            this.financialMasterLoaded = true;

            this.ensureYearTypeEntries();
            this.syncAllFinancialTypesToSections();

            if (this.crdtPrpslSeq) {
              this.loadSavedFinancialData();
            }
          },
          error: (error) => {
            console.error('Financial values load error:', error);
          },
        });
      },
      error: (error) => {
        console.error('Financial types load error:', error);
      },
    });
  }

  loadSavedFinancialData(): void {
    if (!this.crdtPrpslSeq || !this.financialMasterLoaded) {
      return;
    }

    this.creditService
      .getFinancialData(this.crdtPrpslSeq, this.FINANCIAL_DATA_TYPE)
      .subscribe({
      next: (response: any) => {
        const savedData = this.extractFinancialData(response);

        if (!savedData) {
          return;
        }

        this.mapSavedFinancialData(savedData);
      },
      error: (error) => {
        console.error('Get financial data error:', error);
      },
    });
  }

  private extractFinancialData(response: any): any {
    if (Array.isArray(response)) {
      return response[0] ?? null;
    }

    if (Array.isArray(response?.data)) {
      return response.data[0] ?? null;
    }

    return response?.data ?? response ?? null;
  }

  private mapSavedFinancialData(savedData: any): void {
  

   this.institutionName =
  String(
    savedData?.instNm ??
    this.institutionName ??
    ''
  ).trim();

this.demographicModel = {
  ...this.demographicModel,

  institutionName:
    this.institutionName,

  reportingCurrency:
    savedData?.rprtngCrncy ?? null,

  financialYearEnd:
    this.toInputDate(
      savedData?.fnclYrEnd
    ),

  isConsolidated:
    Number(
      savedData?.cnsldtdFncl
    ) === 1,
};

    const savedList = (savedData.crdtFnclDtaLst || []).filter(
      (item: any) => Number(item.delFlg) !== 1,
    );

    const savedYears: string[] = [
      ...new Set<string>(
        savedList
          .map((item: any): string =>
            item?.fncYr !== null && item?.fncYr !== undefined
              ? String(item.fncYr)
              : '',
          )
          .filter((year: string): boolean => year.length > 0),
      ),
    ];

    if (savedYears.length > 0) {
      this.years = savedYears.sort(
        (first: string, second: string) =>
          Number(second) - Number(first),
      );
    }

    /*
     * Audited / Unaudited is year-wise, not section-wise.
     * Derive one value per year from the saved rows.
     */
    this.financialTypeByYear = {};

    this.years.forEach((year: string) => {
      const savedTypesForYear =
        savedList
          .filter(
            (item: any) =>
              String(item?.fncYr) === year
          )
          .map(
            (item: any) =>
              this.normalizeFinancialType(
                item?.fnclType
              )
          )
          .filter(
            (
              type: number | null
            ): type is number =>
              type !== null
          );

      this.financialTypeByYear[year] =
        savedTypesForYear.length
          ? savedTypesForYear[0]
          : null;

      const uniqueTypes =
        new Set(savedTypesForYear);

      if (uniqueTypes.size > 1) {
        console.warn(
          `Multiple financial types were saved for ${year}. ` +
          `Using ${this.getFinancialTypeLabel(
            this.financialTypeByYear[year]
          )} for all sections.`
        );
      }
    });

    this.financialSections.forEach((section) => {
      section.checked = false;
      section.typeByYear = this.createEmptyTypeByYear();

      section.rows.forEach((row: any) => {
        row.checked = false;
        row.recordSequenceByYear = {};
        row.recordTypeByYear = {};
      });
    });

savedList.forEach((savedItem: any) => {
  let matchedSection: any = null;
  let matchedRow: any = null;

  for (const section of this.financialSections) {
    const row = section.rows.find(
      (item: any) =>
        Number(item.fncValSeq) ===
        Number(
          savedItem.valSeq ??
          savedItem.refValSeq,
        ),
    );

    if (row) {
      matchedSection = section;
      matchedRow = row;
      break;
    }
  }

  if (!matchedSection || !matchedRow) {
    console.warn(
      'Financial row not found for saved value:',
      savedItem,
    );

    return;
  }

  const year =
    String(savedItem.fncYr);

  /*
   * fnclType:
   * 0 = Audited
   * 1 = Unaudited
   */
  const auditedType =
    this.normalizeFinancialType(
      savedItem.fnclType,
    );

  matchedSection.checked = true;
  matchedRow.checked = true;

  matchedSection.typeByYear[year] =
    this.financialTypeByYear[year] ??
    auditedType;

  this.demographicModel[
    this.getFieldKey(
      matchedRow.key,
      year,
    )
  ] = savedItem.val;

  matchedRow.recordSequenceByYear = {
    ...(matchedRow.recordSequenceByYear || {}),

    [year]:
      savedItem.crdtFnclDtaSeq,
  };

  matchedRow.recordTypeByYear = {
    ...(matchedRow.recordTypeByYear || {}),

    [year]:
      auditedType,
  };
});

    this.ensureYearTypeEntries();
    this.syncAllFinancialTypesToSections();
  }

  toggle(section: any): void {
    section.expanded = !section.expanded;
  }

  toggleSectionRows(section: any): void {
    section.rows.forEach((row: any) => {
      row.checked = section.checked;
    });
  }

  getFieldKey(rowKey: string, year: string): string {
    return `${rowKey}_${year}`;
  }

  /**
   * Total for a section's column (year), computed in code
   * by summing the entered values of its selected rows.
   * This value is NOT read from the Excel file.
   */
  getSectionTotal(
    section: any,
    year: string
  ): number {
    return (section?.rows || []).reduce(
      (sum: number, row: any) => {
        if (!row.checked) {
          return sum;
        }

        const value = Number(
          this.demographicModel[
            this.getFieldKey(
              row.key,
              year
            )
          ]
        );

        return (
          sum +
          (Number.isFinite(value)
            ? value
            : 0)
        );
      },
      0
    );
  }

  /**
   * Total for a section identified by its title (matched
   * case/space/hyphen-insensitively). Returns 0 if the
   * section is not present.
   */
  private getSectionTotalByTitle(
    title: string,
    year: string
  ): number {
    const target =
      this.normalizeLabel(title);

    const section =
      this.financialSections.find(
        (item: any) =>
          this.normalizeLabel(
            item.title
          ) === target
      );

    return section
      ? this.getSectionTotal(
          section,
          year
        )
      : 0;
  }

  /**
   * Total Liabilities = Total Current Liabilities +
   * Total Non-Current Liabilities (computed in code).
   */
  getTotalLiabilities(
    year: string
  ): number {
    return (
      this.getSectionTotalByTitle(
        'Current Liabilities',
        year
      ) +
      this.getSectionTotalByTitle(
        'Non Current Liabilities',
        year
      )
    );
  }

  /**
   * Total Liabilities and Equity = Total Liabilities +
   * Total Equity (computed in code).
   */
  getTotalLiabilitiesAndEquity(
    year: string
  ): number {
    return (
      this.getTotalLiabilities(year) +
      this.getSectionTotalByTitle(
        'Equity',
        year
      )
    );
  }

  getFinancialTypeLabel(value: any): string {
    const type = this.normalizeFinancialType(value);

    if (type === 0) {
      return 'Audited';
    }

    if (type === 1) {
      return 'Unaudited';
    }

    return '-';
  }

  /** Translation key for the Audited / Unaudited badge (display only). */
  getFinancialTypeLabelKey(value: any): string {
    const type = this.normalizeFinancialType(value);

    if (type === 0) {
      return 'FINANCIAL_DATA.AUDITED';
    }

    if (type === 1) {
      return 'FINANCIAL_DATA.UNAUDITED';
    }

    return '-';
  }

  getReportingCurrencyLabel(value: any): string {
    const currency = Number(value);

    if (value !== null && value !== undefined && value !== '') {
      if (currency === 0) {
        return 'PKR';
      }

      if (currency === 1) {
        return 'USD';
      }
    }

    return '-';
  }

  onFormChange(value: any): void {
    this.demographicModel = {
      ...this.demographicModel,
      ...value,
    };
  }

  onSubmit(value: any): void {
    this.onFormChange(value);

    this.saveFinancialData(
      this.sectionId
    );
  }

  saveFinancialData(
    requestedSectionId:
      string = this.sectionId
  ): void {
    if (!this.editable) {
      return;
    }

    const resultSectionId =
      this.normalizeSectionId(
        requestedSectionId ||
          this.sectionId
      );

    if (!resultSectionId) {
      console.error(
        'Financial Data section ID is missing.'
      );

      return;
    }

    const proposalSequence =
      Number(
        this.crdtPrpslSeq ??
          this.creditService
            .getCrdtPrpslSeq() ??
          0
      );

    if (
      !Number.isFinite(
        proposalSequence
      ) ||
      proposalSequence <= 0
    ) {
      const message =
this.translate.instant('FINANCIAL_DATA.ERR_PROPOSAL_SEQ_REQUIRED');

      console.error(message);

      this.reportValidationError(
        message,
        resultSectionId
      );

      return;
    }

    const institutionName =
      String(
        this.demographicModel
          ?.institutionName ??
          this.institutionName ??
          ''
      ).trim();

    if (!institutionName) {
      const message =
this.translate.instant('FINANCIAL_DATA.ERR_INSTITUTION_NAME_REQUIRED');

      console.error(message);

      this.reportValidationError(
        message,
        resultSectionId
      );

      return;
    }

    const reportingCurrencyValue =
      this.demographicModel
        .reportingCurrency;

    if (
      reportingCurrencyValue === null ||
      reportingCurrencyValue ===
        undefined ||
      reportingCurrencyValue === ''
    ) {
      const message =
this.translate.instant('FINANCIAL_DATA.ERR_REPORTING_CURRENCY_REQUIRED');

      console.error(message);

      this.reportValidationError(
        message,
        resultSectionId
      );

      return;
    }

    const reportingCurrency =
      Number(
        reportingCurrencyValue
      );

    if (
      reportingCurrency !== 0 &&
      reportingCurrency !== 1
    ) {
      const message =
this.translate.instant('FINANCIAL_DATA.ERR_REPORTING_CURRENCY_INVALID');

      console.error(message);

      this.reportValidationError(
        message,
        resultSectionId
      );

      return;
    }

    const financialYearEnd =
      this.formatApiDate(
        this.demographicModel
          .financialYearEnd
      );

    if (!financialYearEnd) {
      const message =
this.translate.instant('FINANCIAL_DATA.ERR_FINANCIAL_YEAR_END_REQUIRED');

      console.error(message);

      this.reportValidationError(
        message,
        resultSectionId
      );

      return;
    }

    const missingType =
      this.findMissingColumnType();

    if (missingType) {
      const message =
        this.translate.instant('FINANCIAL_DATA.ERR_SELECT_TYPE_FOR_YEAR', {
          year: missingType.year,
        });

      console.error(message);

      this.reportValidationError(
        message,
        resultSectionId
      );

      return;
    }

    const financialDataList =
      this.buildFinancialDataList();

    if (!financialDataList.length) {
      const message =
this.translate.instant('FINANCIAL_DATA.ERR_NO_VALUES');

      console.error(message);

      this.reportValidationError(
        message,
        resultSectionId
      );

      return;
    }

    /*
     * The current year and the two before it are mandatory: each must be
     * added and have at least one active (non-deleted) value.
     */
    const currentYear =
      new Date().getFullYear();

    const missingYears =
      [currentYear - 2, currentYear - 1, currentYear].filter(
        (requiredYear) =>
          !financialDataList.some(
            (item) =>
              item.delFlg === 0 &&
              Number(item.fncYr) ===
                requiredYear
          )
      );

    if (missingYears.length) {
      const message =
        this.translate.instant(
          'FINANCIAL_DATA.ERR_CURRENT_YEAR_REQUIRED',
          {
            years: missingYears.join(', '),
            requiredYears: [
              currentYear - 2,
              currentYear - 1,
              currentYear,
            ].join(', '),
          }
        );

      console.error(message);

      this.reportValidationError(
        message,
        resultSectionId
      );

      return;
    }

    this.institutionName =
      institutionName;

    const payload:
      SaveCreditFinancialDataPayload = {
      crdtPrpslSeq:
        proposalSequence,

      rprtngCrncy:
        reportingCurrency,

      fnclYrEnd:
        financialYearEnd,

      cnsldtdFncl:
        this.toNumberFlag(
          this.demographicModel
            .isConsolidated
        ),

      instNm:
        institutionName,

      crdtFnclDtaLst:
        financialDataList,
    };

    console.log(
      'Financial Data Save Payload:',
      {
        sectionId:
          resultSectionId,
        payload,
      }
    );

    this.creditService
      .saveFinancialData(
        payload
      )
      .subscribe({
        next: (response: any) => {
          console.log(
            'Financial Data saved successfully:',
            response
          );

          this.toastr.success(
            this.translate.instant('FINANCIAL_DATA.SAVED_SUCCESS')
          );

          const returnedSequence =
            Number(
              response?.crdtPrpslSeq ??
                response?.data
                  ?.crdtPrpslSeq ??
                proposalSequence
            );

          if (
            Number.isFinite(
              returnedSequence
            ) &&
            returnedSequence > 0
          ) {
            this.crdtPrpslSeq =
              returnedSequence;

            this.creditService
              .setCrdtPrpslSeq(
                returnedSequence
              );
          }

          /*
           * Report the exact same section ID that
           * triggered the save. The parent waits
           * for this result before moving next.
           */
          this.creditService
            .reportSectionSaveResult({
              sectionId:
                resultSectionId,

              status:
                'saved',
            });

          this.removedYears = [];

          // Income Statement / Extra Terms reuse this section's years and
          // Audited / Unaudited status — let them pick up the change.
          this.financialProfileService.notifyChanged();

          /*
           * Reload only after the parent has
           * received save confirmation.
           */
          this.loadSavedFinancialData();
        },

        error: (error: any) => {
          console.error(
            'Save financial data error:',
            error
          );

          const errorMessage =
            error?.error?.message ??
            this.translate.instant('FINANCIAL_DATA.ERR_SAVE');

          this.toastr.error(
            errorMessage
          );

          this.creditService
            .reportSectionSaveResult({
              sectionId:
                resultSectionId,

              status:
                'api-error',

              message:
                errorMessage,
            });
        },
      });
  }

  private reportValidationError(
    message: string,
    requestedSectionId:
      string = this.sectionId
  ): void {
    const resultSectionId =
      this.normalizeSectionId(
        requestedSectionId ||
          this.sectionId
      );

    if (!resultSectionId) {
      console.error(
        'Cannot report Financial Data validation error because sectionId is missing.',
        message
      );

      return;
    }

    this.toastr.warning(message);

    this.creditService
      .reportSectionSaveResult({
        sectionId:
          resultSectionId,

        status:
          'validation-error',

        message,
      });
  }

private buildFinancialDataList(): CreditFinancialDataItemDto[] {
  const records: CreditFinancialDataItemDto[] = [];

  // Removed years no longer render in `years`, but their previously saved
  // rows still need a delFlg 1 record — union the two so they aren't
  // silently dropped from the save payload.
  const yearsToProcess = [
    ...this.years,
    ...this.removedYears.filter(
      (year) => !this.years.includes(year),
    ),
  ];

  this.financialSections.forEach((section: any) => {
    section.rows.forEach((row: any) => {
      yearsToProcess.forEach((year: string) => {
        const fieldKey = this.getFieldKey(
          row.key,
          year,
        );

        const enteredValue =
          this.demographicModel[fieldKey];

        const hasValue =
          enteredValue !== null &&
          enteredValue !== undefined &&
          enteredValue !== '';

        const numericValue =
          Number(enteredValue);

        const isSelected =
          row.checked === true;

        const previousSequence =
          this.toNullableNumber(
            row.recordSequenceByYear?.[year],
          );

        /*
         * 0 = Audited
         * 1 = Unaudited
         */
        const selectedFinancialType =
          this.normalizeFinancialType(
            this.financialTypeByYear?.[year],
          );

        const previousFinancialType =
          this.normalizeFinancialType(
            row.recordTypeByYear?.[year],
          );

        const isRemovedYear =
          this.removedYears.includes(year);

        /*
         * Create or update active record.
         */
        if (
          !isRemovedYear &&
          isSelected &&
          hasValue &&
          Number.isFinite(numericValue)
        ) {
          records.push({
            crdtFnclDtaSeq:
              previousSequence,

            valSeq:
              Number(row.fncValSeq),

            val:
              numericValue,

            delFlg:
              0,

            fnclType:
              selectedFinancialType ?? 0,

            fncYr:
              Number(year),

            // Fixed backend value
            typ:
              1,
          });

          return;
        }

        /*
         * Delete existing record.
         */
        if (previousSequence !== null) {
          records.push({
            crdtFnclDtaSeq:
              previousSequence,

            valSeq:
              Number(row.fncValSeq),

            val:
              0,

            delFlg:
              1,

            fnclType:
              selectedFinancialType ??
              previousFinancialType ??
              0,

            fncYr:
              Number(year),

            // Fixed backend value
            typ:
              1,
          });
        }
      });
    });
  });

  return records;
}

  private findMissingColumnType(): {
    sectionTitle: string;
    year: string;
  } | null {
    for (const year of this.years) {
      const hasSelectedValue =
        this.financialSections.some(
          (section: any) =>
            section.rows.some(
              (row: any) => {
                const value =
                  this.demographicModel[
                    this.getFieldKey(
                      row.key,
                      year
                    )
                  ];

                return (
                  row.checked === true &&
                  value !== null &&
                  value !== undefined &&
                  value !== ''
                );
              }
            )
        );

      if (
        hasSelectedValue &&
        this.normalizeFinancialType(
          this.financialTypeByYear?.[year]
        ) === null
      ) {
        return {
          sectionTitle:
            'Financial Data',
          year,
        };
      }
    }

    return null;
  }

  private createEmptyTypeByYear(): Record<string, number | null> {
    return this.years.reduce(
      (
        result:
          Record<string, number | null>,
        year: string
      ) => {
        result[year] =
          this.normalizeFinancialType(
            this.financialTypeByYear?.[year]
          );

        return result;
      },
      {},
    );
  }

  private ensureYearTypeEntries(): void {
    this.years.forEach(
      (year: string) => {
        if (
          this.financialTypeByYear[year] ===
          undefined
        ) {
          this.financialTypeByYear[year] =
            null;
        }
      }
    );

    Object.keys(
      this.financialTypeByYear
    ).forEach((year: string) => {
      if (!this.years.includes(year)) {
        delete this.financialTypeByYear[
          year
        ];
      }
    });
  }

  private syncFinancialTypeToSections(
    year: string
  ): void {
    const type =
      this.normalizeFinancialType(
        this.financialTypeByYear?.[year]
      );

    this.financialSections.forEach(
      (section: any) => {
        section.typeByYear =
          section.typeByYear || {};

        section.typeByYear[year] =
          type;
      }
    );
  }

  private syncAllFinancialTypesToSections():
    void {
    this.ensureYearTypeEntries();

    this.years.forEach(
      (year: string) =>
        this.syncFinancialTypeToSections(
          year
        )
    );
  }

  private normalizeFinancialType(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const type = Number(value);

    return type === 0 || type === 1 ? type : null;
  }

  private toNumberFlag(value: any): number {
    return value === true || value === 'true' || value === 1 ? 1 : 0;
  }

  private toInputDate(value: any): string {
    if (!value) {
      return '';
    }

    return String(value).slice(0, 10);
  }

  private formatApiDate(value: any): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value.slice(0, 10);
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString().slice(0, 10);
  }
  private normalizeSectionId(
    value: unknown
  ): string {
    return String(
      value ?? ''
    ).trim();
  }

  private toNullableNumber(
  value: unknown
): number | null {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return null;
  }

  const numericValue =
    Number(value);

  return Number.isFinite(
    numericValue
  )
    ? numericValue
    : null;
}
}